/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { type FC } from 'react'
import BaseBreadcrumb from 'react-bootstrap/Breadcrumb'
import { Helmet } from 'react-helmet'
import { Link, useMatches } from 'react-router-dom'
import styled from 'styled-components'

export type Props = {
  className?: string
}

const StyledBreadcrumb = styled(BaseBreadcrumb)`
  & > .breadcrumb {
    font-weight: bold;
    border-radius: 0px;
    box-shadow: none;
    background-color: transparent;
    padding: 15px 0;
    margin: 0;
    border: none;
    text-transform: uppercase;
    font-size: 11px !important;

    a {
      text-decoration: none;
      color: ${props => props.theme.colors.primary};
    }

    .breadcrumb-item {
      display: flex;
    }

    & > .breadcrumb-item {
      & + .breadcrumb-item {
        &:before {
          font-size: 11px;
          content: var(--bs-breadcrumb-divider, "/");
        }
      }

      &.active {
        font-weight: 500;
      }
    }
  }
`

/**
 * Breadcrumb is described here.
 *
 * @example
 * ```jsx
 * <Breadcrumb />
 * ```
 *
 * @since 06/02/2024
 * @link https://react-styleguidist.js.org/docs/documenting.html
 */
const Breadcrumb: FC<Props> = ({ className }) => {
  const matches = useMatches()
  const crumbs = matches
    .filter((match: any) => Boolean(match.handle?.crumb))

  if (crumbs.length <= 1) {
    return null
  }

  return (
    <StyledBreadcrumb className={className}>
      {crumbs?.map(({ id, pathname }, index) => (
        <StyledBreadcrumb.Item
          key={(index + id).toString()}
          href={pathname}
          linkAs={Link}
          linkProps={{ to: pathname }}
          active={index === crumbs.length - 1}
        >
          {index === crumbs.length - 1 && <Helmet title={id} />}
          {id}
        </StyledBreadcrumb.Item>
      ))}
    </StyledBreadcrumb>
  )
}

export default Breadcrumb
